import React, { useState, useContext, memo } from 'react';
import classNames from 'classnames';
import HeaderContext from '../../../../contexts/Header.context';
import { withBlokWrapper } from '../../../BlokWrapper/BlokWrapper';
import { Theme } from '../../../../interfaces/enums/Theme.enum';
import StoryblokLinkProvider from '../../../Utils/StoryblokLinkProvider/StoryblokLinkProvider';
import CustomLink from '../../../CustomLink/CustomLink';

import { Blok } from '../../../../interfaces/Blok.interface';
import { LinkProps } from '../../../../interfaces/LinkProps.interface';
import { StoryblokLink } from '../../../../interfaces/StoryblokLink.interface';
import { StoryblokAsset } from '../../../../interfaces/StoryblokAsset.interface';
import SbImage from '../../../SbImage/SbImage';

interface MainNavDropdownPrimaryItem extends Blok {
  label: string;
  link: StoryblokLink;
  _uid: string;
}

interface MainNavDropdownSecondaryItem extends Blok {
  heading: string;
  body: string;
  link: StoryblokLink;
  _uid: string;
}

export interface MainNavDropdownBlokProps extends Blok {
  primary_heading: string;
  primary_links: MainNavDropdownPrimaryItem[];
  secondary_heading: string;
  secondary_links: MainNavDropdownSecondaryItem[];
  icon: StoryblokAsset;
  cta: LinkProps[];
}

interface MainNavDropdownProps {
  dropdown: MainNavDropdownBlokProps[];
}

const MainNavDropdown: React.FC<MainNavDropdownProps> = ({ dropdown }) => {
  const { navIsUnpinned } = useContext(HeaderContext);

  return (
    <div
      className={classNames(
        {
          block: !navIsUnpinned,
          hidden: navIsUnpinned,
        },
        `invisible opacity-0 group-hover:visible group-hover:opacity-100 duration-0 group-hover:duration-[200ms] ease-in-out cursor-default transition-all left-0 right-0 top-[100%] absolute z-[51] bg-[white]`
      )}
    >
      <div className="container mx-auto py-16">
        <div className="flex justify-start">
          <div className="border-border/light border-r pr-20">
            {dropdown[0].primary_heading && (
              <p className="text-text/body-accent text-sm font-normal pb-4 uppercase">
                {dropdown[0].primary_heading}
              </p>
            )}
            <ul className="grid grid-cols-2-tight grid-flow-row-dense gap-x-6">
              {!!dropdown &&
                dropdown[0]?.primary_links?.map(({ link, label, _uid }) => (
                  <li className="pb-2" key={_uid}>
                    <CustomLink
                      url={link?.cached_url}
                      label={label}
                      className="nav-link font-normal text-text/body hover:text-text/body-accent py-2 block"
                    >
                      {label}
                    </CustomLink>
                  </li>
                ))}
            </ul>
          </div>
          <div className="pl-20">
            {dropdown[0].secondary_heading && (
              <p className="text-text/body-accent text-sm font-normal pb-6 uppercase">
                {dropdown[0].secondary_heading}
              </p>
            )}
            <ul>
              {!!dropdown &&
                dropdown[0]?.secondary_links?.map(
                  ({ link, heading, body, _uid }) => (
                    <li className="pb-8" key={_uid}>
                      <StoryblokLinkProvider
                        link={link}
                        render={(renderedLink) => (
                          <CustomLink
                            url={renderedLink}
                            label={heading}
                            className="nav-link flex items-center space-x-4 text-text/body prose-p:hover:text-text/body-accent text-sm"
                          >
                            <div className="h-[40px] w-[40px] flex justify-center items-center border rounded-full border-[#009bef] bg-[#E5F4F9]">
                              {dropdown[0].icon && (
                                <SbImage
                                  asset={dropdown[0].icon}
                                  options={{
                                    objectFit: 'contain',
                                    width: 24,
                                    height: 24,
                                    sizes: { base: 24 },
                                  }}
                                />
                              )}
                            </div>
                            <div>
                              <p className="font-bold">{heading}</p>
                              <span className="font-normal">{body}</span>
                            </div>
                          </CustomLink>
                        )}
                      />
                    </li>
                  )
                )}
            </ul>
            {dropdown[0]?.cta && dropdown[0]?.cta[0] && (
              <StoryblokLinkProvider
                link={dropdown[0]?.cta[0].link}
                render={(link) => (
                  <div className="flex items-center space-x-2">
                    <CustomLink
                      url={link}
                      label={dropdown[0]?.cta[0].label}
                      className="text-text/body-primary flex items-center"
                    >
                      {dropdown[0]?.cta[0].label}
                      <span className="block h-[24px] w-[24px] bg-[url('/svgs/arrow.svg')] bg-no-repeat ml-2" />
                    </CustomLink>
                  </div>
                )}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(withBlokWrapper(MainNavDropdown, Theme.LIGHT));
